import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { SparkIcon } from '../icons/SparkIcon';
import { useReducedMotion } from '../ui/useReducedMotion';

const USE_CASES = [
  {
    id: 'feedback', name: 'Customer feedback',
    description: 'Understand what customers love, and what needs to change.',
    followup: 'What would have made setup easier?',
  },
  {
    id: 'import', name: 'Import existing forms',
    description: 'Import from Typeform, Google Forms, or Microsoft Forms.',
    followup: 'Your forms. Now AI-native.',
  },
  {
    id: 'enquiries', name: 'Inbound enquiries',
    description: 'Qualify inbound enquiries and find your next customer.',
    followup: 'How many people will be joining?',
  },
];

const IMPORT_PROVIDERS = [
  { id: 'typeform', name: 'Typeform' },
  { id: 'google-forms', name: 'Google Forms' },
  { id: 'microsoft-forms', name: 'Microsoft Forms' },
];

/** Render desktop artwork without downloading promotional images on mobile. */
function DesktopArtwork({ src, width, height, alt = '', className = '' }: {
  src: string; width: number; height: number; alt?: string; className?: string;
}) {
  return <picture className={className}>
    <source media="(min-width: 960px)" srcSet={src} />
    <img src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=" alt={alt} width={width} height={height} decoding="async" />
  </picture>;
}

/** Show the source forms converging on holito in one compact, monochrome graphic. */
function ImportGraphic() {
  return <div className="auth-import-flow">
    <div className="auth-source-forms">
      {IMPORT_PROVIDERS.map(provider => <div className="auth-source-form" key={provider.id}>
        <DesktopArtwork
          className={`auth-import-logo auth-import-logo--${provider.id}`}
          src={`/auth/logos/${provider.id}.svg`}
          alt={provider.name}
          width={32}
          height={32}
        />
        <span className="auth-source-rule" />
        <span className="auth-source-field" />
        <span className="auth-source-rule" />
        <span className="auth-source-field" />
      </div>)}
    </div>
    <svg className="auth-import-path" viewBox="0 0 160 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M24 1c0 22 56 4 56 38M80 1v38M136 1c0 22-56 4-56 38m-6-6 6 6 6-6" />
    </svg>
    <div className="auth-import-destination">
      <DesktopArtwork src="/holito-logo-black.svg" width={198} height={52} />
    </div>
  </div>;
}

/** Show a respondent's answer beside the contextual question it prompts. */
function EnquiryGraphic() {
  return <div className="auth-question-screen">
    <DesktopArtwork className="auth-question-brand" src="/holito-logo-black.svg" width={198} height={52} />
    <p className="auth-screen-question">What are you planning?</p>
    <p className="auth-screen-answer">A team offsite.</p>
  </div>;
}

/** A three-slide introduction that pauses while hovered or used with a keyboard. */
export function AuthProductPreview() {
  const reducedMotion = useReducedMotion();
  const [active, setActive] = useState(0);
  const [keyboardFocused, setKeyboardFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const pointerFocus = useRef(false);

  useEffect(() => {
    if (keyboardFocused || reducedMotion || hovered) return undefined;
    const timer = window.setTimeout(() => setActive(index => (index + 1) % USE_CASES.length), 8000);
    return () => window.clearTimeout(timer);
  }, [active, keyboardFocused, reducedMotion, hovered]);

  /** Changing slides restarts the timer so every selection gets a full interval. */
  function select(index: number) {
    setActive(index);
  }

  /** Keep keyboard focus on the selected tab, including wraparound navigation. */
  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const next = event.key === 'ArrowRight' ? (index + 1) % USE_CASES.length
      : event.key === 'ArrowLeft' ? (index + USE_CASES.length - 1) % USE_CASES.length
        : event.key === 'Home' ? 0 : event.key === 'End' ? USE_CASES.length - 1 : null;
    if (next === null) return;
    event.preventDefault();
    select(next);
    tabs.current[next]?.focus();
  }

  return (
    <aside
      className="auth-product"
      aria-labelledby="auth-product-title"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onPointerDownCapture={() => {
        pointerFocus.current = true;
        setKeyboardFocused(false);
      }}
      onKeyDownCapture={() => {
        pointerFocus.current = false;
        setKeyboardFocused(true);
      }}
      onFocusCapture={() => setKeyboardFocused(!pointerFocus.current)}
      onBlurCapture={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          pointerFocus.current = false;
          setKeyboardFocused(false);
        }
      }}
    >
      <h1 id="auth-product-title" className="auth-product-title">AI-native <span>forms</span></h1>

      <div className="auth-carousel-slides" aria-live="off">
        {USE_CASES.map((useCase, index) => (
          <div
            key={useCase.id}
            id={`auth-panel-${useCase.id}`}
            role="tabpanel"
            aria-labelledby={`auth-tab-${useCase.id}`}
            aria-hidden={index !== active}
            inert={index !== active}
            className="auth-usecase-slide"
            data-active={index === active}
          >
            <p className="auth-product-description">{useCase.description}</p>
            <div className="auth-slide-art">
              <div
                className="auth-art"
                role="img"
                aria-label={useCase.id === 'import'
                  ? 'Import existing forms from Typeform, Google Forms, or Microsoft Forms by pasting a public form link.'
                  : useCase.id === 'enquiries'
                    ? `Inbound enquiries preview: hollie asks "What are you planning?" The respondent answers "A team offsite." AI follow-up: ${useCase.followup}`
                  : `${useCase.name} preview: hollie adapts with a follow-up: ${useCase.followup} Respond in text, voice, or video.`}
              >
                <div className={`auth-poster auth-poster--${useCase.id}`} aria-hidden="true">
                  <div className={`auth-poster-portrait${useCase.id === 'feedback' ? '' : ' auth-poster-portrait--graphic'}`}>
                    {useCase.id === 'feedback'
                      ? <DesktopArtwork src="/auth/feedback.webp" width={1024} height={1536} />
                      : useCase.id === 'import' ? <ImportGraphic /> : <EnquiryGraphic />}
                    <span className="auth-poster-label">{useCase.id === 'import' ? 'Import forms' : useCase.name}</span>
                  </div>
                  <div className="auth-poster-copy">
                    <p className="auth-poster-question">{useCase.followup}</p>
                    <span className="auth-poster-caption"><SparkIcon />{useCase.id === 'import' ? 'Paste a link' : 'AI follow-up'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="auth-carousel-controls">
        <button type="button" className="auth-carousel-arrow" aria-label="Previous use case" onClick={() => select((active + USE_CASES.length - 1) % USE_CASES.length)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m14 6-6 6 6 6" /></svg>
        </button>
        <div className="auth-usecases" role="tablist" aria-label="Form use cases">
          {USE_CASES.map((useCase, index) => (
            <button
              key={useCase.id}
              ref={element => { tabs.current[index] = element; }}
              type="button"
              id={`auth-tab-${useCase.id}`}
              role="tab"
              aria-label={useCase.name}
              aria-selected={index === active}
              aria-controls={`auth-panel-${useCase.id}`}
              tabIndex={index === active ? 0 : -1}
              onClick={() => select(index)}
              onKeyDown={event => handleKeyDown(event, index)}
            ><span className="auth-usecase-dot" aria-hidden="true" /></button>
          ))}
        </div>
        <button type="button" className="auth-carousel-arrow" aria-label="Next use case" onClick={() => select((active + 1) % USE_CASES.length)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m10 6 6 6-6 6" /></svg>
        </button>
      </div>
    </aside>
  );
}
